'use client'

import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { toast } from 'react-toastify'
import { useCreateUserMutation } from 'graphql/generated/graphql'
import {
  consumePendingGeneralAccount,
  hasSawBusinessPricing,
  isBusinessRegistrationPath,
  markSawBusinessPricing,
  peekPendingGeneralAccount,
} from '@/lib/signupFlow'

const LEAVE_TOAST =
  'You can log in as a home cook anytime with the email you just used.'

export default function PendingGeneralAccountWatcher() {
  const pathname = usePathname()
  const [createUser] = useCreateUserMutation({ fetchPolicy: 'no-cache' })
  const finishing = useRef(false)

  useEffect(() => {
    if (!peekPendingGeneralAccount()) return

    if (isBusinessRegistrationPath(pathname)) {
      markSawBusinessPricing()
      return
    }

    if (!hasSawBusinessPricing() || finishing.current) return

    finishing.current = true
    const pending = consumePendingGeneralAccount()
    if (!pending) {
      finishing.current = false
      return
    }

    const finish = async () => {
      if (pending.source === 'email') {
        try {
          await createUser({
            variables: {
              input: {
                first_name: pending.first_name,
                last_name: pending.last_name,
                email_address: pending.email,
                password_hash: pending.password,
              },
            },
          })
        } catch {
          // Email may already exist if they finished business registration.
        }
      }
      toast.info(LEAVE_TOAST)
      finishing.current = false
    }

    void finish()
  }, [pathname, createUser])

  useEffect(() => {
    const onPageHide = () => {
      if (!peekPendingGeneralAccount() || !hasSawBusinessPricing() || finishing.current) return
      finishing.current = true
      const pending = consumePendingGeneralAccount()
      if (pending?.source === 'email') {
        void createUser({
          variables: {
            input: {
              first_name: pending.first_name,
              last_name: pending.last_name,
              email_address: pending.email,
              password_hash: pending.password,
            },
          },
        }).catch(() => {})
      }
    }
    window.addEventListener('pagehide', onPageHide)
    return () => window.removeEventListener('pagehide', onPageHide)
  }, [createUser])

  return null
}
